<script setup>
import { computed, onMounted, ref, watch } from 'vue';
import { useRoute } from 'vue-router';
import EventMediaGallery from '@/js/core/components/parts/calendar/EventMediaGallery.vue';
import FullPageModal from '@/js/core/components/parts/FullPageModal.vue';
import NavigationPanel from '@/js/core/components/parts/navigation-panel/NavigationPanel.vue';
import EventModalCtaSidebar from '@/js/core/components/pages/event-modal/EventModalCtaSidebar.vue';
import EventModalLivestreamRegion from '@/js/core/components/pages/event-modal/EventModalLivestreamRegion.vue';
import EventModalMetaList from '@/js/core/components/pages/event-modal/EventModalMetaList.vue';
import { useEventLivestreamDisplay } from '@/js/core/components/pages/event-modal/useEventLivestreamDisplay';
import { useEventsListStore } from '@/js/core/stores/events-list-store';
import { useLoadingStore } from '@/js/core/stores/loading-store';
import { useTimezoneStore } from '@/js/core/stores/timezone-store';
import { useModalClose } from '@/js/core/composables/useModalClose';
import { useNotFoundRedirect } from '@/js/core/composables/useNotFoundRedirect';
import {
  getEventFilterGroup,
  hasAdvanceRegistration,
  hasLivestream,
} from '@/js/core/utils/calendar-events';
import { scopeEventToOccurrenceGroup } from '@/js/core/utils/event-modal-meta';
import { serviceApiIdFromCalendarEventPageSlug } from '@/js/core/utils/calendar-event-route';

const route = useRoute();
const eventsStore = useEventsListStore();
const loadingStore = useLoadingStore();
const timezoneStore = useTimezoneStore();
const { closeModal } = useModalClose({ fallbackRoute: { name: 'calendar' } });
const { redirectOnNotFound } = useNotFoundRedirect();

const eventPageSlug = computed(() => String(route.params.eventPageSlug || ''));

const event = computed(() => eventsStore.findEventBySlug(eventPageSlug.value));

const fetchError = ref('');
const isReady = computed(() => !!event.value);

const eventTimezone = computed(() => timezoneStore.effectiveTimezone);

const occurrenceServiceApiId = computed(() =>
  serviceApiIdFromCalendarEventPageSlug(eventPageSlug.value)
);

const scopedEvent = computed(() =>
  scopeEventToOccurrenceGroup(event.value, occurrenceServiceApiId.value, eventTimezone.value)
);

const eventClock = computed(() => timezoneStore.clock);
const {
  livestreamDetails,
  muxPlaybackId,
  shouldRenderMuxPlayer,
  shouldRenderVimeoPlayer,
  shouldRenderLivestreamRegion,
  registrationUrl,
  zoomUrl,
  vimeoJoinUrl,
} = useEventLivestreamDisplay(event, eventTimezone, eventClock);

const loadEventData = async slug => {
  if (!slug || event.value) {
    return;
  }

  loadingStore.start();
  fetchError.value = '';
  try {
    await eventsStore.fetchEvent(slug);
  } catch (err) {
    if (redirectOnNotFound(err)) return;

    fetchError.value = 'Could not load event details.';
  } finally {
    loadingStore.complete();
  }
};

const refreshHydratedEventData = slug => {
  if (!slug || !eventsStore.shouldRefreshHydratedEvent(slug)) {
    return;
  }

  eventsStore.refreshEventDetail(slug);
};

watch(
  () => eventPageSlug.value,
  newSlug => {
    if (newSlug) loadEventData(newSlug);
  },
  { immediate: true }
);

watch(
  () => [eventPageSlug.value, isReady.value],
  ([slug, ready]) => {
    if (ready) {
      refreshHydratedEventData(slug);
    }
  },
  { immediate: true }
);

onMounted(() => {
  timezoneStore.hydrateFromCurrentProfile();
});

const BLOCK_HTML = /<(?:p|div|section|article|figure|img|ul|ol|li|table|blockquote|dl|h[1-6])\b/i;
const SOLE_PARAGRAPH = /^\s*<p>([\s\S]*)<\/p>\s*$/;
const LINE_SEPARATOR = /(?:<br\s*\/?>|\n)+/i;

// Services API descriptions are plain text with bare newlines. Rendering those
// as <br> packs them at the raw line-height, which is unreadable for the long
// instructor/critic lists on review events, and no CSS can space a <br>.
const formatPlainTextLines = text =>
  `<div class="event-modal__description-lines">` +
  text
    .split(LINE_SEPARATOR)
    .map(line => line.trim())
    .filter(Boolean)
    .map(line => `<p>${line}</p>`)
    .join('') +
  `</div>`;

const formatDescription = text => {
  if (!text) return '';

  const trimmed = text.trim();

  // Sync re-parses descriptions through DOMDocument, which wraps bare text in a
  // lone <p>. Unwrap that so plain text isn't mistaken for structured HTML.
  const sole = trimmed.match(SOLE_PARAGRAPH);
  const unwrapped = sole && !BLOCK_HTML.test(sole[1]) ? sole[1] : trimmed;

  if (!BLOCK_HTML.test(unwrapped)) {
    return formatPlainTextLines(unwrapped);
  }

  return trimmed.replace(/[ \t\r\n]*\n[ \t\r\n]*/g, (match, offset, str) => {
    const before = str[offset - 1];
    const after = str[offset + match.length];
    if (before === undefined || after === undefined || (before === '>' && after === '<')) {
      return match;
    }
    const newlineCount = (match.match(/\n/g) || []).length;
    return newlineCount > 1 ? '<br><br>' : '<br>';
  });
};

// Ordered html/gallery blocks so each gallery renders at its original
// position in the content.
const descriptionBlocks = computed(() => {
  const blocks = event.value?.content_blocks;

  if (blocks?.length) {
    return blocks;
  }

  const fallback = [];

  if (event.value?.media_gallery?.length) {
    fallback.push({ type: 'gallery', items: event.value.media_gallery });
  }

  const html = event.value?.content || event.value?.description;

  if (html) {
    fallback.push({ type: 'html', html });
  }

  return fallback;
});
</script>

<template>
  <FullPageModal
    v-if="isReady || fetchError"
    :title="event?.title || 'Event'"
    wrapper-class="is-event-modal"
    :data-filter-group="getEventFilterGroup(event?.category)"
    @close="closeModal"
  >
    <template #header-top>
      <div v-if="event" class="event-modal__meta">
        <span class="event-modal__category">{{ event.category }}</span>
        <span v-if="hasLivestream(event)" class="event-modal__livestream-tag">Livestream</span>
        <span v-if="hasAdvanceRegistration(event)" class="event-modal__advanced-reg">
          Advance Registration
        </span>
      </div>
    </template>

    <div v-if="fetchError" class="event-modal__error">
      {{ fetchError }}
    </div>
    <div v-else-if="event" class="event-modal__container">
      <EventModalLivestreamRegion
        v-if="shouldRenderLivestreamRegion"
        :livestream-details="livestreamDetails"
        :mux-playback-id="muxPlaybackId"
        :should-render-mux-player="shouldRenderMuxPlayer"
        :should-render-vimeo-player="shouldRenderVimeoPlayer"
        :title="event.title"
      />

      <EventModalMetaList :event="scopedEvent" :timezone="eventTimezone" :clock="eventClock" />

      <EventModalCtaSidebar
        :event="event"
        :registration-url="registrationUrl"
        :zoom-url="zoomUrl"
        :vimeo-join-url="vimeoJoinUrl"
      />

      <div class="event-modal__description">
        <div class="event-modal__description-wrapper">
          <template v-for="(block, index) in descriptionBlocks" :key="index">
            <EventMediaGallery v-if="block.type === 'gallery'" :items="block.items" />

            <div
              v-else
              class="event-modal__description-block"
              v-html="formatDescription(block.html)"
            ></div>
          </template>
        </div>
      </div>
    </div>

    <template #footer>
      <div class="view-tray-navbar__wrapper">
        <NavigationPanel />
      </div>
    </template>
  </FullPageModal>
</template>
